import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState, type FormEvent } from "react";
import { useRoleGuard } from "@/hooks/use-role-guard";
import { useServerFn } from "@tanstack/react-start";
import { Pencil, Trash2, Plus, Check } from "lucide-react";
import {
  listPrograms,
  upsertProgram,
  deleteProgram,
  convertProgram,
  listGrantSubtypes,
  listGrantsBySubtype,
  getGrantAutofill,
  type ProgramInput,
  type ProgramFilters,
} from "@/lib/admin.functions";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";

export const Route = createFileRoute("/admin/programs")({
  head: () => ({
    meta: [
      { title: "Programs - Admin - DeckScore" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: ProgramsPage,
});

type Program = {
  id: string;
  name: string;
  type: string | null;
  thesis: string | null;
  stage_focus: string[] | null;
  sector_focus: string[] | null;
  region: string | null;
  submit_method: string | null;
  contact_email: string | null;
  crm_note: string | null;
  active: boolean;
  notes: string | null;
  owner_user_id: string | null;
  ref_code: string | null;
  open_to_program?: boolean | null;
  created_at?: string | null;
  grant_subtype?: string | null;
  grant_id?: string | null;
  opening_date?: string | null;
  closing_date?: string | null;
  is_rolling?: boolean | null;
  amount?: string | null;
  application_url?: string | null;
  website_url?: string | null;
};




function toCSV(v: string[] | null | undefined) {
  return (v ?? []).join(", ");
}
function fromCSV(s: string): string[] {
  return s
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);
}

function ProgramsPage() {
  const { status: roleStatus } = useRoleGuard(["admin", "super_admin"]);
  const runList = useServerFn(listPrograms);
  const runUpsert = useServerFn(upsertProgram);
  const runDelete = useServerFn(deleteProgram);
  const runConvert = useServerFn(convertProgram);

  const [programs, setPrograms] = useState<Program[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Program | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filters, setFilters] = useState<ProgramFilters>({
    source: "all",
    status: "all",
    sort: "name_asc",
  });

  async function refresh() {
    setLoading(true);
    const { programs } = await runList({ data: filters });
    setPrograms(programs as Program[]);
    setLoading(false);
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.source, filters.status, filters.type, filters.region, filters.sort]);

  function openNew() {
    setEditing({
      id: "",
      name: "",
      type: "accelerator",
      thesis: "",
      stage_focus: [],
      sector_focus: [],
      region: "",
      submit_method: "",
      contact_email: "",
      crm_note: "",
      active: true,
      notes: "",
      owner_user_id: null,
      ref_code: null,
    });
    setDialogOpen(true);
  }

  function openEdit(p: Program) {
    setEditing(p);
    setDialogOpen(true);
  }

  async function handleDelete(p: Program) {
    if (!confirm(`Delete program "${p.name}"? This cannot be undone.`)) return;
    await runDelete({ data: { id: p.id } });
    await refresh();
  }

  async function toggleActive(p: Program) {
    await runUpsert({
      data: {
        id: p.id,
        name: p.name,
        type: p.type,
        thesis: p.thesis,
        stage_focus: p.stage_focus,
        sector_focus: p.sector_focus,
        region: p.region,
        submit_method: p.submit_method,
        contact_email: p.contact_email,
        crm_note: p.crm_note,
        notes: p.notes,
        active: !p.active,
      },
    });
    await refresh();
  }

  async function handleConvert(p: Program) {
    if (!confirm(`Convert "${p.name}" to live? They'll start receiving paid submission targeting.`)) return;
    await runConvert({ data: { id: p.id } });
    await refresh();
  }

  if (roleStatus !== "ok") return null;
  return (
    <>

      <div className="flex items-end justify-between mb-6">
        <div>
          <p className="font-script text-accent text-[22px] leading-none">program directory</p>
          <h1 className="font-display text-[28px] font-semibold mt-2">Programs</h1>
        </div>
        <Button onClick={openNew} className="rounded-full bg-accent text-accent-ink hover:bg-accent-press">
          <Plus className="w-4 h-4 mr-1" /> Add program
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4 items-end">
        <div className="flex flex-col gap-1">
          <Label className="text-[12px] text-ink-tertiary">Source</Label>
          <Select value={filters.source ?? "all"} onValueChange={(v) => setFilters((f) => ({ ...f, source: v as ProgramFilters["source"] }))}>
            <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="investor">Investor-registered</SelectItem>
              <SelectItem value="curated">Curated</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1">
          <Label className="text-[12px] text-ink-tertiary">Status</Label>
          <Select value={filters.status ?? "all"} onValueChange={(v) => setFilters((f) => ({ ...f, status: v as ProgramFilters["status"] }))}>
            <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="active">Active</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1">
          <Label className="text-[12px] text-ink-tertiary">Type</Label>
          <Select value={filters.type ?? "all"} onValueChange={(v) => setFilters((f) => ({ ...f, type: v === "all" ? null : v }))}>
            <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="accelerator">Accelerator</SelectItem>
              <SelectItem value="incubator">Incubator</SelectItem>
              <SelectItem value="vc">VC</SelectItem>
              <SelectItem value="grant">Grant</SelectItem>

            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1">
          <Label className="text-[12px] text-ink-tertiary">Region</Label>
          <Input
            className="w-[160px]"
            placeholder="e.g. India"
            value={filters.region ?? ""}
            onChange={(e) => setFilters((f) => ({ ...f, region: e.target.value || null }))}
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label className="text-[12px] text-ink-tertiary">Sort</Label>
          <Select value={filters.sort ?? "name_asc"} onValueChange={(v) => setFilters((f) => ({ ...f, sort: v as ProgramFilters["sort"] }))}>
            <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="name_asc">Name (A-Z)</SelectItem>
              <SelectItem value="created_desc">Newest first</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="bg-surface border border-line rounded-2xl overflow-hidden">
        <table className="w-full text-[14px]">
          <thead className="bg-surface-sunken text-ink-secondary">
            <tr>
              <th className="text-left px-4 py-3 font-medium">Name</th>
              <th className="text-left px-4 py-3 font-medium">Source</th>
              <th className="text-left px-4 py-3 font-medium">Type</th>
              <th className="text-left px-4 py-3 font-medium">Region</th>
              <th className="text-left px-4 py-3 font-medium">Active</th>
              <th className="text-right px-4 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-ink-tertiary">
                  Loading…
                </td>
              </tr>
            ) : programs.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-ink-tertiary">
                  No programs match these filters.
                </td>
              </tr>
            ) : (
              programs.map((p) => {
                const isInvestor = !!p.owner_user_id;
                return (
                  <tr key={p.id} className="border-t border-line">
                    <td className="px-4 py-3 font-medium">
                      {p.name}
                      {p.ref_code && (
                        <span className="ml-2 text-[11px] font-mono text-ink-tertiary">ref:{p.ref_code}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-ink-secondary">
                      {isInvestor ? (
                        <span className="inline-flex items-center gap-1.5">
                          <span className="inline-flex items-center rounded-full bg-accent/10 text-ink px-2 py-0.5 text-[11.5px] font-medium">Investor</span>
                          {p.open_to_program && (
                            <span
                              className="inline-flex items-center rounded-full bg-accent-mint text-ink px-2 py-0.5 text-[11px] font-medium"
                              title="Investor opted in to DeckScore matching"
                            >
                              Open to matching
                            </span>
                          )}
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-full bg-surface-sunken text-ink-secondary px-2 py-0.5 text-[11.5px]">Curated</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-ink-secondary">{p.type ?? "-"}</td>
                    <td className="px-4 py-3 text-ink-secondary">{p.region ?? "-"}</td>
                    <td className="px-4 py-3">
                      <Switch checked={p.active} onCheckedChange={() => toggleActive(p)} />
                    </td>
                    <td className="px-4 py-3 text-right">
                      {isInvestor && !p.active && (
                        <button
                          onClick={() => handleConvert(p)}
                          className="inline-flex items-center gap-1 text-[13px] text-[#1F7A3C] hover:opacity-80 px-2 py-1"
                          title="Set active=true so this investor receives paid submission targeting"
                        >
                          <Check className="w-3.5 h-3.5" /> Convert to live
                        </button>
                      )}
                      <button
                        onClick={() => openEdit(p)}
                        className="inline-flex items-center gap-1 text-[13px] text-ink-secondary hover:text-ink px-2 py-1 ml-1"
                      >
                        <Pencil className="w-3.5 h-3.5" /> Edit
                      </button>
                      <button
                        onClick={() => handleDelete(p)}
                        className="inline-flex items-center gap-1 text-[13px] text-[#C65C4E] hover:opacity-80 px-2 py-1 ml-1"
                      >
                        <Trash2 className="w-3.5 h-3.5" /> Delete
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <ProgramDialog
          key={editing.id || "new"}
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          initial={editing}
          onSave={async (input) => {
            await runUpsert({ data: input });
            setDialogOpen(false);
            await refresh();
          }}
        />
      )}
    </>
  );
}


function ProgramDialog({
  open,
  onOpenChange,
  initial,
  onSave,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  initial: Program;
  onSave: (input: ProgramInput) => Promise<void>;
}) {
  const [name, setName] = useState(initial.name);
  const [type, setType] = useState<string>(initial.type ?? "accelerator");
  const [thesis, setThesis] = useState(initial.thesis ?? "");
  const [stage, setStage] = useState(toCSV(initial.stage_focus));
  const [sector, setSector] = useState(toCSV(initial.sector_focus));
  const [region, setRegion] = useState(initial.region ?? "");
  const [submit, setSubmit] = useState(initial.submit_method ?? "");
  const [contact, setContact] = useState(initial.contact_email ?? "");
  const [crm, setCrm] = useState(initial.crm_note ?? "");
  const [active, setActive] = useState(initial.active);
  const [notes, setNotes] = useState(initial.notes ?? "");
  const [saving, setSaving] = useState(false);

  // Grant-specific state
  const [grantSubtype, setGrantSubtype] = useState<string | null>(initial.grant_subtype ?? null);
  const [grantId, setGrantId] = useState<string | null>(initial.grant_id ?? null);
  const [openingDate, setOpeningDate] = useState<string>(initial.opening_date ?? "");
  const [closingDate, setClosingDate] = useState<string>(initial.closing_date ?? "");
  const [isRolling, setIsRolling] = useState<boolean>(!!initial.is_rolling);
  const [amount, setAmount] = useState<string>(initial.amount ?? "");
  const [applicationUrl, setApplicationUrl] = useState<string>(initial.application_url ?? "");
  const [websiteUrl, setWebsiteUrl] = useState<string>(initial.website_url ?? "");

  const [subtypes, setSubtypes] = useState<{ value: string; label: string; count: number }[]>([]);
  const [grantOptions, setGrantOptions] = useState<{ id: string; name: string }[]>([]);
  const [autofilled, setAutofilled] = useState<boolean>(!!initial.grant_id);

  const runListSubtypes = useServerFn(listGrantSubtypes);
  const runListGrants = useServerFn(listGrantsBySubtype);
  const runGetAutofill = useServerFn(getGrantAutofill);

  useEffect(() => {
    if (type !== "grant") return;
    (async () => {
      const { subtypes } = await runListSubtypes();
      setSubtypes(subtypes);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  useEffect(() => {
    if (type !== "grant" || !grantSubtype) {
      setGrantOptions([]);
      return;
    }
    (async () => {
      const { grants } = await runListGrants({ data: { subtype: grantSubtype } });
      setGrantOptions(grants as { id: string; name: string }[]);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, grantSubtype]);

  async function handlePickGrant(id: string) {
    setGrantId(id);
    const { autofill } = await runGetAutofill({ data: { grantId: id } });
    setName(autofill.name);
    if (autofill.grant_subtype) setGrantSubtype(autofill.grant_subtype);
    setRegion(autofill.region ?? "");
    setSector(autofill.sector_focus.join(", "));
    setThesis(autofill.thesis ?? "");
    setAmount(autofill.amount ?? "");
    setOpeningDate(autofill.opening_date ?? "");
    setClosingDate(autofill.closing_date ?? "");
    setIsRolling(!!autofill.is_rolling);
    setApplicationUrl(autofill.application_url ?? "");
    setWebsiteUrl(autofill.website_url ?? "");
    setContact(autofill.contact_email ?? "");
    if (autofill.notes) setNotes(autofill.notes);
    setSubmit((prev) => prev || "portal");
    setAutofilled(true);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    try {
      const isGrant = type === "grant";
      await onSave({
        id: initial.id || undefined,
        name: name.trim(),
        type,
        thesis: thesis.trim() || null,
        stage_focus: fromCSV(stage),
        sector_focus: fromCSV(sector),
        region: region.trim() || null,
        submit_method: submit.trim() || (isGrant ? "portal" : null),
        contact_email: contact.trim() || null,
        crm_note: crm.trim() || null,
        active,
        notes: notes.trim() || null,
        grant_subtype: isGrant ? grantSubtype : null,
        grant_id: isGrant ? grantId : null,
        opening_date: isGrant && openingDate ? openingDate : null,
        closing_date: isGrant && closingDate ? closingDate : null,
        is_rolling: isGrant ? isRolling : null,
        amount: isGrant ? (amount.trim() || null) : null,
        application_url: isGrant ? (applicationUrl.trim() || null) : null,
        website_url: isGrant ? (websiteUrl.trim() || null) : null,
      });
    } finally {
      setSaving(false);
    }
  }

  const isGrant = type === "grant";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{initial.id ? "Edit program" : "Add program"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="type">Type</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger id="type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="accelerator">Accelerator</SelectItem>
                <SelectItem value="incubator">Incubator</SelectItem>
                <SelectItem value="vc">VC</SelectItem>
                <SelectItem value="grant">Grant</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="region">Region</Label>
            <Input id="region" value={region} onChange={(e) => setRegion(e.target.value)} />
          </div>

          {isGrant && (
            <>
              <div>
                <Label htmlFor="grant-subtype">Sub-type</Label>
                <Select
                  value={grantSubtype ?? ""}
                  onValueChange={(v) => {
                    setGrantSubtype(v);
                    setGrantId(null);
                  }}
                >
                  <SelectTrigger id="grant-subtype">
                    <SelectValue placeholder="Select sub-type" />
                  </SelectTrigger>
                  <SelectContent>
                    {subtypes.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.label} ({s.count})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="grant-name">Grant name</Label>
                <Select
                  value={grantId ?? ""}
                  onValueChange={(v) => handlePickGrant(v)}
                  disabled={!grantSubtype}
                >
                  <SelectTrigger id="grant-name">
                    <SelectValue placeholder={grantSubtype ? "Select a grant" : "Pick sub-type first"} />
                  </SelectTrigger>
                  <SelectContent>
                    {grantOptions.map((g) => (
                      <SelectItem key={g.id} value={g.id}>
                        {g.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {autofilled && (
                <div className="sm:col-span-2 rounded-lg bg-[#FDECE4] text-ink px-3 py-2 text-[12.5px]">
                  Autofilled from the grants database - editable before you save.
                </div>
              )}
            </>
          )}

          <div className="sm:col-span-2">
            <Label htmlFor="name">Name</Label>
            <Input id="name" value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="thesis">{isGrant ? "Description / thesis" : "Thesis"}</Label>
            <Textarea id="thesis" rows={2} value={thesis} onChange={(e) => setThesis(e.target.value)} />
          </div>

          {isGrant && (
            <>
              <div>
                <Label htmlFor="amount">Amount</Label>
                <Input id="amount" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="e.g. Up to $50,000" />
              </div>
              <div className="flex items-center gap-3 mt-6">
                <Switch id="is-rolling" checked={isRolling} onCheckedChange={setIsRolling} />
                <Label htmlFor="is-rolling" className="cursor-pointer">Rolling intake</Label>
              </div>
              <div>
                <Label htmlFor="opening-date">Opening date</Label>
                <Input id="opening-date" type="date" value={openingDate} onChange={(e) => setOpeningDate(e.target.value)} />
              </div>
              <div>
                <Label htmlFor="closing-date">Closing date</Label>
                <Input id="closing-date" type="date" value={closingDate} onChange={(e) => setClosingDate(e.target.value)} />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="application-url">Application link</Label>
                <Input id="application-url" value={applicationUrl} onChange={(e) => setApplicationUrl(e.target.value)} placeholder="https://" />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="website-url">Website</Label>
                <Input id="website-url" value={websiteUrl} onChange={(e) => setWebsiteUrl(e.target.value)} placeholder="https://" />
              </div>
            </>
          )}

          {!isGrant && (
            <>
              <div>
                <Label htmlFor="stage">Stage focus (comma-separated)</Label>
                <Input id="stage" value={stage} onChange={(e) => setStage(e.target.value)} placeholder="pre-seed, seed" />
              </div>
              <div>
                <Label htmlFor="sector">Sector focus (comma-separated)</Label>
                <Input id="sector" value={sector} onChange={(e) => setSector(e.target.value)} placeholder="AI, fintech" />
              </div>
            </>
          )}
          {isGrant && (
            <div className="sm:col-span-2">
              <Label htmlFor="sector">Sector focus (comma-separated)</Label>
              <Input id="sector" value={sector} onChange={(e) => setSector(e.target.value)} placeholder="AI, fintech" />
            </div>
          )}

          <div className="sm:col-span-2">
            <Label htmlFor="submit">Submit method <span className="text-ink-tertiary text-[12px]">(internal - not shown to founders)</span></Label>
            <Input id="submit" value={submit} onChange={(e) => setSubmit(e.target.value)} />
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="contact">Contact email <span className="text-ink-tertiary text-[12px]">(internal)</span></Label>
            <Input id="contact" type="email" value={contact} onChange={(e) => setContact(e.target.value)} />
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="crm">CRM note <span className="text-ink-tertiary text-[12px]">(internal)</span></Label>
            <Textarea id="crm" rows={2} value={crm} onChange={(e) => setCrm(e.target.value)} />
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea id="notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
          <div className="sm:col-span-2 flex items-center gap-3">
            <Switch id="active" checked={active} onCheckedChange={setActive} />
            <Label htmlFor="active" className="cursor-pointer">Active</Label>
          </div>
          <DialogFooter className="sm:col-span-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving} className="bg-accent text-accent-ink hover:bg-accent-press">
              {saving ? "Saving…" : "Save program"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );

}
